import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Heart, Video, Sparkles, MessageCircle, Bell, RotateCcw, Star } from "lucide-react";

const ICON = {
  interest: { icon: Heart, cls: "bg-pink-500/20 text-pink-300" },
  match: { icon: Sparkles, cls: "bg-amber-500/20 text-amber-300" },
  confirmed: { icon: Video, cls: "bg-emerald-500/20 text-emerald-300" },
  message: { icon: MessageCircle, cls: "bg-fuchsia-500/20 text-fuchsia-300" },
  nudge: { icon: RotateCcw, cls: "bg-amber-500/20 text-amber-300" },
  checkin: { icon: Star, cls: "bg-sky-500/20 text-sky-300" },
};

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Activity() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: notifs = [], isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
  });

  useEffect(() => {
    (async () => { try { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); } catch {} })();
  }, [qc]);

  const go = (n) => {
    if (n.type === "confirmed" || n.type === "message" || n.type === "checkin") navigate(`/chat/${n.data?.match_id}`);
    else if (n.type === "match" || n.type === "nudge") navigate(`/call/${n.data?.match_id}`);
    else if (n.type === "interest") navigate(`/events/${n.data?.event_id}`);
  };

  return (
    <div className="fade-up">
      <h1 className="font-display font-black text-4xl">Activity</h1>
      <p className="text-slate-300 mt-1">Interests, matches and plans — all in one place.</p>

      {isLoading ? (
        <div className="space-y-2 mt-6">{[...Array(5)].map((_, i) => <div key={i} className="h-16 rounded-2xl bg-white/5 animate-pulse" />)}</div>
      ) : notifs.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center mt-10">
          <Bell className="h-10 w-10 text-amber-400/60 mx-auto mb-3" />
          <p className="text-slate-300">Nothing yet. Express interest in someone at an event to get things moving.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-2">
          {notifs.map((n) => {
            const cfg = ICON[n.type] || { icon: Bell, cls: "bg-white/10 text-white" };
            return (
              <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => go(n)}
                className={`w-full glass rounded-2xl p-4 flex items-center gap-4 text-left hover:border-amber-500/40 transition-all ${!n.read ? "border-amber-400/30" : ""}`}>
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${cfg.cls}`}><cfg.icon className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold leading-tight">{n.title}</p>
                  <p className="text-sm text-slate-300 truncate">{n.body}</p>
                </div>
                <span className="text-[11px] text-slate-400 shrink-0">{timeAgo(n.created_at)}</span>
                {!n.read && <span className="h-2 w-2 rounded-full bg-pink-500 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
